import { el, html } from "@/functions";
import { customElement } from "./custom-element-decorator";
import { PjElement } from "./pj-element";

@customElement("pj-chips")
export class PjChipsElement extends PjElement {
  static observedAttributes = ["readonly", "name", "list", "placeholder"];
  static formAssociated = true;

  private readonly _shadow = this.attachShadow({ mode: "open" });
  private readonly _internals = this.attachInternals();
  private readonly _input = el("input", { props: { name: "newChip", autocomplete: "off" } });
  private _chips: readonly string[] = [];
  private _isReadOnly = false;
  private _touchHold: ReturnType<typeof setTimeout> | null = null;

  get readOnly() {
    return this._isReadOnly;
  }
  set readOnly(value) {
    this._isReadOnly = value;
    this._render();
  }

  get list(): string {
    return this._input.getAttribute("list") ?? "";
  }
  set list(value: string) {
    this._input.setAttribute("list", value);
  }

  _name: string = "";
  get name(): string {
    return this._name;
  }
  set name(value: string) {
    this._name = value;
    if (this.isConnected) {
      this._internals.setFormValue(this.value);
    }
  }

  get placeholder(): string {
    return this._input.placeholder;
  }
  set placeholder(value: string) {
    this._input.placeholder = value;
  }

  get value(): string {
    return JSON.stringify(this._chips);
  }
  set value(newValue: string) {
    this._chips = JSON.parse(newValue || "[]");
    this._render();
  }

  get valueAsArray(): readonly string[] {
    return this._chips;
  }
  set valueAsArray(value) {
    this._chips = [...value];
    this._render();
  }

  protected initialize(first: boolean): void | Promise<void> {
    if (first && !!this.textContent.trim()) {
      const firstChips: string[] = [];
      for (const child of this.children) {
        firstChips.push(child.textContent);
      }

      if (this.children.length === 0 && !!this.textContent.trim()) {
        for (const part of this.textContent.split(",")) {
          firstChips.push(part.trim());
        }
      }

      this._chips = firstChips;
    }

    const eventSource = this._shadow as {
      addEventListener: HTMLElement["addEventListener"];
      removeEventListener: HTMLElement["removeEventListener"];
    };
    eventSource.addEventListener("keydown", this._onKeyDown, { signal: this.disconnected });
    eventSource.addEventListener("click", this._onInnerClick, {
      signal: this.disconnected,
    });
    this.addEventListener("click", this._onOuterClick, { signal: this.disconnected });
    eventSource.addEventListener("contextmenu", this._onContextMenu, {
      signal: this.disconnected,
    });
    eventSource.addEventListener("touchstart", this._onTouchStart, { signal: this.disconnected });
    eventSource.addEventListener("touchend", this._onTouchEnd, { signal: this.disconnected });
    eventSource.addEventListener("touchcancel", this._onTouchEnd, { signal: this.disconnected });
    this._input.addEventListener("focusout", () => this._saveCurrentChip(false), {
      signal: this.disconnected,
    });
    this._render();
    this._internals.setFormValue(this.value);
  }

  private _render() {
    this._shadow.innerHTML = `
      <style>
        :host {
          min-width: 8rch;

          color: FieldText;
          
          display: inline-flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: var(--spacing);

          &:not(:host(:state(readonly))) {
            cursor: text;
            background-color: Field;
          }
        }

        input {
          background-color: transparent;
          border: none;
          outline: none;
          font-size: 1rem;
          padding: 0;
          min-height: var(--interactive-height);
          min-width: var(--interactive-width);
          flex: 1 1 auto;
        }

        ul, li {
          list-style-type: none;
          margin: 0;
          padding: 0;
        }

        ul {
          display: contents;
        }

        button {
          background: unset;
          color: inherit;
          font-size: inherit;
          cursor: pointer;
          border: none;
          user-select: none;

          display: inline-flex;
          gap: var(--spacing);
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          min-height: var(--interactive-height);
          min-width: var(--interactive-width);
          font-size: 1rem;
      
          &:is(li>button)[name="delete"]::after {
            content: "⊗";
          }

          &:is(:host(:state(readonly)) [part="chip"]) {
            justify-content: center;
          }

          &:disabled {
            pointer-events: none;
            &:is(li>button)[name="delete"]::after {
              content: unset;
            }
          }
        }

        [part="chip"] {
          border: 1px solid var(--color-border);
          border-radius: var(--rounding);
          padding: var(--spacing-sm) var(--spacing);
          font-size: var(--text-sm);
          background-color: var(--color-alternating);
          outline: none;

          &:focus, &:active {
            outline: 2px solid white;
          }
        }

        menu {
          margin: auto;
          padding: 0;
          inset: auto;
          top: anchor(bottom);
          justify-self: anchor-center;

          border: 1px solid var(--color-border);
          border-radius: var(--rounding);
          gap: var(--spacing);
          flex-direction: column;
          min-width: 10rem;
          
          &:popover-open {
            display: flex;
          }

          button {            
            &:hover {
              background-color: color-mix(in srgb, light-dark(black, white), transparent 85%);
            }
          }
        }
      </style>

      <ul part="list">
        ${this._chips.map((c, i) => html`<li><button type="button" name="delete" part="chip" data-index="${i}" ${this.readOnly ? "disabled" : ""}>${c}</button></li>`).join("")}
      </ul>

      <menu popover="auto" part="contextmenu">
        <button type="button" part="contextmenuitem" name="edit">Edit</button>
        <button type="button" part="contextmenuitem" name="delete">Delete</button>
      </menu>
      `;

    if (!this.readOnly) {
      this._shadow.append(this._input);
      this._internals.states.delete("readonly");
    } else {
      this._internals.states.add("readonly");
    }
  }

  protected attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    switch (name) {
      case "readonly":
        this._isReadOnly = newValue !== "false" && newValue !== "0" && newValue !== null;
        break;
      case "name":
        this.name = name;
        this._internals.setFormValue(this.value);
        break;
      case "list":
        this.list = newValue || "";
        break;
      case "placeholder":
        this.placeholder = newValue || "";
        break;
      default:
        console.error("Unsupported attribute change notification for", name);
        break;
    }
  }

  public onchange: null | ((event: Event) => void) = null;

  private _notifyChange(): void {
    const event = new Event("change", { bubbles: true });
    this.onchange?.(event);
    this.dispatchEvent(event);
  }

  private _onKeyDown = (event: KeyboardEvent): void => {
    if (this.readOnly) {
      return;
    }
    if (this._input.value && event.key === "Enter") {
      this._saveCurrentChip();
    } else if (this._shadow.activeElement instanceof HTMLButtonElement && keyRemove(event.key)) {
      event.stopPropagation();
      this._delete(this._shadow.activeElement, event);
    } else if (this._shadow.activeElement instanceof HTMLButtonElement && event.key === "Escape") {
      this._input.focus();
    } else if (event.key.match(/^[a-zA-Z0-9]$/) && this._shadow.activeElement !== this._input) {
      this._input.focus();
    } else if (
      this._input.selectionStart === 0 &&
      this._shadow.activeElement === this._input &&
      (event.key === "Backspace" || keyBefore(event.key))
    ) {
      event.stopPropagation();
      this._changeFocus("before");
    } else if (this._shadow.activeElement instanceof HTMLButtonElement && keyBefore(event.key)) {
      this._changeFocus("before");
    } else if (this._shadow.activeElement instanceof HTMLButtonElement && keyAfter(event.key)) {
      this._changeFocus("after");
    }
  };

  private _changeFocus(dir: "before" | "after"): void {
    if (dir === "before") {
      if (this._shadow.activeElement === this._input) {
        this._shadow.querySelector<HTMLButtonElement>("li:last-child > button")?.focus();
      } else {
        this._shadow
          .querySelector<HTMLButtonElement>("li:has( + li:focus-within ) > button")
          ?.focus();
      }
    } else {
      if (this._shadow.activeElement?.matches("li:last-child > button")) {
        this._input.focus();
      } else {
        this._shadow
          .querySelector<HTMLElement>("li:is(:focus,:focus-within) + li > button")
          ?.focus();
      }
    }
  }

  private _onContextMenu = (event: MouseEvent): void => {
    let button: HTMLButtonElement | null = null;

    if (event.target instanceof HTMLElement) {
      button = event.target.closest("button");
    }

    if (!button || button.name !== "delete") {
      return;
    }

    const popover = this._shadow.querySelector<HTMLElement>("[popover]")!;
    popover.dataset["index"] = button.dataset["index"];

    popover.showPopover({ source: button });
    event.preventDefault();
  };

  private _onOuterClick = (event: MouseEvent): void => {
    if (!this._shadow.activeElement && event.target === this) {
      this._input.focus();
    }
  };

  private _onInnerClick = (event: MouseEvent): void => {
    let button: HTMLButtonElement | null = null;

    if (event.target instanceof HTMLElement) {
      button = event.target.closest("button");
    }

    if (!button) {
      return;
    }

    if (button.name === "delete") {
      this._delete(button, event);
    } else if (button.name === "edit") {
      this._edit(button);
    } else {
      throw new Error(`Unimplemented button name: "${button.name}".`);
    }
  };

  private _edit(button: HTMLButtonElement): void {
    // Button is only called from the context menu
    const menu = button.closest("menu");
    const index = Number(menu?.dataset["index"]);
    if (isNaN(index)) {
      throw new Error("Edit button does not have a chip index to edit.");
    }

    const oldChip = this._chips[index];
    const newChips = [...this._chips];
    newChips.splice(index, 1);

    this._input.value = oldChip;
    this._chips = newChips;
    this._internals.setFormValue(this.value);
    this._notifyChange();
    this._render();

    this._input.focus();
    this._input.setSelectionRange(oldChip.length, oldChip.length);
    menu?.hidePopover();
  }

  private _delete(button: HTMLButtonElement, event: MouseEvent | KeyboardEvent): void {
    const newChips = [...this._chips];
    const index = Number(button.dataset["index"] ?? button.closest("menu")?.dataset["index"]);
    newChips.splice(index, 1);
    this._chips = newChips;
    this._internals.setFormValue(this.value);
    this._notifyChange();
    this._render();

    if (event instanceof MouseEvent) {
      // If the user is using mouse input move focus to the input element.
      // Only focus on the nearest chip if the user used keyboard input.
      this._input.focus();
      return;
    }

    const ul = this._shadow.querySelector("ul")!;
    if (ul.children.length > 0 && index === 0) {
      (<HTMLElement>ul.children[0].children[0]).focus();
    } else if (ul.children.length > 0) {
      const forwardBack = "key" in event && event.key === "Delete" ? 0 : -1;
      const toIndex = Math.min(ul.children.length - 1, index + forwardBack);
      const button = <HTMLButtonElement>ul.children[toIndex].children[0];
      button.focus();
    } else {
      this._input.focus();
    }
  }

  private _onTouchStart = (event: TouchEvent): void => {
    if (this._touchHold !== null) {
      clearTimeout(this._touchHold);
    }

    this._touchHold = setTimeout(this._onContextMenu, 1000, event);
  };

  private _onTouchEnd = (event: TouchEvent): void => {
    if (this._touchHold !== null) {
      clearTimeout(this._touchHold);
    }
  };

  private _saveCurrentChip(focusInput: boolean = true) {
    if (!this._input.value.trim()) {
      return;
    }

    this._chips = [...this._chips, this._input.value.trim()];
    this._internals.setFormValue(this.value);
    this._input.value = "";
    this._render();
    this._notifyChange();

    if (focusInput) {
      this._input.focus();
    }
  }
}

function keyBefore(key: string): boolean {
  return key === "ArrowLeft" || key === "ArrowUp";
}

function keyAfter(key: string): boolean {
  return key === "ArrowRight" || key === "ArrowDown";
}

function keyRemove(key: string): boolean {
  return key === "Delete" || key === "Backspace";
}
